"use client"
import { useMedicalCategoryStore } from "@/app/store/useMedicalCategoryData";
import { FitnessGymScroll } from "@/components/FitnessGymScroll";
import HeaderPage from "@/components/header-page";
import MedicalStay from "@/components/medical-stay";
import { useEffect } from "react";
import { useTranslations, useLocale } from 'next-intl';

const metaData = {
  meta_title_fr: "Tourisme Médical en Tunisie - Soins et Bien-être de Classe Mondiale",
  meta_description_fr: "Découvrez les meilleurs centres médicaux et spas de Tunisie. Bénéficiez de soins de qualité, de rééducation et de traitements de bien-être tout en profitant du littoral méditerranéen.",
  meta_keywords_fr: "tourisme médical Tunisie, soins médicaux Tunisie, spas Tunisie, centres médicaux Tunisie, tourisme de santé, rééducation Tunisie, sources thermales, services médicaux Tunisie",
  meta_title_en: "Medical Tourism in Tunisia - World-Class Healthcare and Wellness",
  meta_description_en: "Discover Tunisia's premier medical centers and wellness spas. Experience quality healthcare, rehabilitation, and wellness treatments while enjoying the Mediterranean coastline.",
  meta_keywords_en: "medical tourism Tunisia, healthcare Tunisia, wellness spas Tunisia, medical centers Tunisia, health tourism, rehabilitation Tunisia, thermal springs, medical services Tunisia",
  meta_title_ar: "السياحة العلاجية في تونس - رعاية صحية وعافية عالمية المستوى",
  meta_description_ar: "اكتشف أفضل المراكز الطبية ومنتجعات العافية في تونس. احصل على رعاية صحية عالية الجودة وعلاج تأهيلي وعلاجات العافية مع الاستمتاع بالساحل المتوسطي.",
  meta_keywords_ar: "السياحة العلاجية تونس, الرعاية الصحية تونس, منتجعات العافية تونس, المراكز الطبية تونس, سياحة صحية, إعادة التأهيل تونس, الينابيع الحرارية, الخدمات الطبية تونس",
  meta_title_ru: "Медицинский туризм в Тунисе — Лечение и оздоровление мирового класса",
  meta_description_ru: "Откройте для себя лучшие медицинские центры и спа-курорты Туниса. Получите качественное лечение, реабилитацию и оздоровительные процедуры в сочетании с отдыхом на средиземноморском побережье.",
  meta_keywords_ru: "медицинский туризм Тунис, лечение в Тунисе, спа-курорты Тунис, медицинские центры Тунис, оздоровительный туризм, реабилитация Тунис, термальные источники, медицинские услуги Тунис",
  meta_title_zh: "突尼斯医疗旅游 - 世界级医疗保健与康养服务",
  meta_description_zh: "探索突尼斯顶级医疗中心和康养水疗中心。享受优质医疗保健、康复治疗和养生服务，同时欣赏地中海海岸风光。",
  meta_keywords_zh: "突尼斯医疗旅游, 突尼斯医疗保健, 突尼斯康养水疗, 突尼斯医疗中心, 健康旅游, 突尼斯康复治疗, 温泉疗养, 突尼斯医疗服务",
  meta_title_ja: "チュニジア医療ツーリズム - 世界クラスのヘルスケアとウェルネス",
  meta_description_ja: "チュニジアの一流医療センターとウェルネススパをご紹介します。地中海沿岸の美しい環境で、質の高い医療、リハビリテーション、ウェルネストリートメントをお楽しみください。",
  meta_keywords_ja: "チュニジア医療ツーリズム, チュニジア医療, チュニジアウェルネススパ, チュニジア医療センター, ヘルスツーリズム, チュニジアリハビリ, 温泉療法, チュニジア医療サービス",
  meta_title_ko: "튀니지 의료관광 - 세계적 수준의 의료 서비스와 웰니스",
  meta_description_ko: "튀니지 최고의 의료센터와 웰니스 스파를 만나보세요. 지중해 연안의 아름다운 환경에서 양질의 의료 서비스, 재활 치료, 웰니스 트리트먼트를 경험하세요.",
  meta_keywords_ko: "튀니지 의료관광, 튀니지 의료 서비스, 튀니지 웰니스 스파, 튀니지 의료센터, 건강 관광, 튀니지 재활 치료, 온천 요법, 튀니지 의료 서비스"
};

export default function MedicalStaysClient() {
    const t = useTranslations();
    const locale = useLocale();
    const { categories, error, loading, fetchCategories } = useMedicalCategoryStore();
    
    useEffect(() => {
        fetchCategories()
    }, [fetchCategories])
    
    // SEO Metadata - runs on mount and locale change
    useEffect(() => {
        const suffix = locale === 'fr' ? 'fr' : locale;
        const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || 'https://tunisiagotravel.com';
        
        // Update title
        const title = metaData[`meta_title_${suffix}` as keyof typeof metaData] || metaData.meta_title_en;
        document.title = title;
        
        // Update or create meta description
        let metaDesc = document.querySelector('meta[name="description"]');
        if (!metaDesc) {
            metaDesc = document.createElement('meta');
            metaDesc.setAttribute('name', 'description');
            document.head.appendChild(metaDesc);
        }
        const description = metaData[`meta_description_${suffix}` as keyof typeof metaData] || metaData.meta_description_en;
        metaDesc.setAttribute('content', description);
        
        // Update or create meta keywords
        let metaKeywords = document.querySelector('meta[name="keywords"]');
        if (!metaKeywords) {
            metaKeywords = document.createElement('meta');
            metaKeywords.setAttribute('name', 'keywords');
            document.head.appendChild(metaKeywords);
        }
        const keywords = metaData[`meta_keywords_${suffix}` as keyof typeof metaData] || metaData.meta_keywords_en;
        metaKeywords.setAttribute('content', keywords);
        
        // Update or create canonical URL
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.setAttribute('rel', 'canonical');
            document.head.appendChild(canonical);
        }
        canonical.setAttribute('href', `${baseUrl}/${locale}/medical-stays`);
    }, [locale]);
    
    const renderMedicalCard = (item: any, index: number) => (
        <MedicalStay item={item} />
    )
    
    return (
        <div dir={locale === 'ar' ? 'rtl' : 'ltr'}>
            <HeaderPage
                title={t('navigation.medicalStays')}
                breadcrumbs={[
                    { label: t('common.home'), href: "/" },
                    { label: t('navigation.medicalStays'), href: "/medical-stays" },
                ]}
            />
            {loading && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                    <div
                        key={i}
                        className="rounded-xl overflow-hidden border animate-pulse"
                    >
                        <div className="h-48 bg-gray-200" />
                        <div className="p-4 space-y-3">
                            <div className="h-4 bg-gray-200 rounded w-3/4" />
                            <div className="h-3 bg-gray-200 rounded w-full" />
                            <div className="h-3 bg-gray-200 rounded w-5/6" />
                        </div>
                    </div>
                ))}
            </div>}
            {error && (
                <p className="text-center text-red-500">
                    {t('medicalStays.errorMessage')}
                </p>
            )}
            {!loading && !error && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 p-6">
                    {categories.map((item, index) => (
                        <div key={index}>
                            {renderMedicalCard(item, index)}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}